import { originationCapPercent } from './origination.js';

/**
 * What a particular question on the form means, for the practice run's guide.
 *
 * WHY THIS EXISTS: the point of the practice run is that nobody should need a training session.
 * A tour that walks the pages gets somebody to the end of the form; it does not answer the
 * question they actually stop on, which is always a specific box — "does this have to add to
 * 100?", "why has this filled itself in?", "what is a wall?". So clicking any field swaps the
 * guide over to explaining that field.
 *
 * TWO LAYERS, PATH THEN TYPE. A path entry is for a question whose meaning is particular to this
 * firm's form. A type entry is for a kind of control, and covers every field of that kind
 * including ones an administrator adds tomorrow — which matters, because most of the form is
 * admin-configurable and a purely path-keyed list would go stale the first time somebody adds a
 * question.
 *
 * NOTHING IS INVENTED. Every rule stated here is one the form actually enforces, and the numbers
 * come from settings rather than from this file — the origination cap is read from
 * `settings/general`, so the text cannot disagree with the validation. Where we genuinely do not
 * know what a firm-added question means, the guide says what KIND of question it is and stops,
 * because a confident wrong explanation is worse than none.
 */

/** Questions whose meaning is specific, keyed by the path they live at. */
const BY_PATH = {
  'header.practiceGroupId': {
    title: 'Practice Group',
    body: [
      'The group the matter belongs to. It is worth getting right first: it fills in the Practice Group Leader below, and it is what the firm reports on.',
      'If more than one group is involved, put the main one here and add the others under **Additional Practice Groups**.',
    ],
  },
  'header.practiceGroupLeaderName': {
    title: 'Practice Group Leader',
    body: [
      'This fills itself in when you choose a Practice Group above — you do not normally type here.',
      'Override it only if the usual leader is not the right person for this matter.',
    ],
  },
  'header.responsiblePartnerUid': {
    title: 'Responsible Partner',
    body: [
      'The partner answerable for the matter. Start typing a surname and pick from the list rather than typing a name freehand, so the record points at a real person on the roster.',
    ],
  },
  'origination.workingAttorneys': {
    title: 'Origination Split',
    body: [
      'Who gets origination credit, and how much each. **The percentages must add up to exactly 100** — the form adds them up as you type and will not let a split through that does not.',
      'Add a row per attorney with **+ Add working attorney**. One attorney taking the whole matter still needs a row.',
    ],
  },
  'custom.select_current_client': {
    title: 'Select Current Client',
    body: [
      'If the firm already acts for this client, find them here and their details come across rather than being retyped.',
      'Search by name or by client number — both work. Picking the existing record is the difference between one client in the system and two slightly different ones.',
    ],
  },
  'client.name': {
    title: 'Name of Client',
    body: [
      'The client as they should appear on the file and on the bill — the full legal name, not the shorthand everyone uses.',
      'For an existing client, use **Select Current Client** above instead of typing it again.',
    ],
  },
  'wallBuilder.practiceGroupIds': {
    title: 'Wall Builder — practice groups permitted access',
    body: [
      'An ethical wall: who inside the firm may see this file. Most matters need nothing here.',
      'Naming groups or people restricts the file to them. It is a deliberate decision, not a default — leave it alone unless somebody has told you this matter needs walling.',
    ],
  },
  'wallBuilder.personUids': {
    title: 'Wall Builder — individual people permitted access',
    body: [
      'Named people who may see this file, on top of any groups above. Again: only when the matter genuinely needs a wall.',
    ],
  },
  'areasOfLaw': {
    title: 'Area of law',
    body: [
      'How the matter is classified for the firm’s reporting. The list is maintained by the firm — if nothing fits, choose Other and say why in the box that appears.',
    ],
  },
  'representation.description': {
    title: 'Brief description of case or nature of work',
    body: [
      'What the firm is actually being asked to do, in a sentence or two. Plain language is fine.',
      'This is the line most people read first when they open the matter later, so it is worth more than three words.',
    ],
  },
  'priorRepresentation.whyChangedCounsel': {
    title: 'Why did the client change counsel?',
    body: [
      'Answer this one plainly. A client who had another lawyer on the same matter is exactly what this page exists to catch, and a vague answer here is the kind of thing that surfaces much later at a much worse moment.',
    ],
  },
};

/** What a kind of control is, for every field of that kind including ones added later. */
const BY_TYPE = {
  originationSplit: {
    title: 'Origination split',
    body: ['Percentages that must add up to exactly 100. The form adds them up as you type.'],
  },
  clientSearch: {
    title: 'Client lookup',
    body: [
      'Searches the firm’s existing clients by name and by client number at once. Pick a result and the details come across instead of being retyped.',
    ],
  },
  userSelect: {
    title: 'A person from the roster',
    body: [
      'Start typing a surname and pick from the list. Choosing from the roster rather than typing a name means the record points at a real person.',
    ],
  },
  peopleMultiSelect: {
    title: 'Several people',
    body: ['Add as many as apply, one at a time. Each is picked from the firm roster.'],
  },
  practiceGroupSelect: {
    title: 'A practice group',
    body: ['One group, from the list the firm maintains.'],
  },
  practiceGroupMultiSelect: {
    title: 'Several practice groups',
    body: ['Add as many as apply. Leave it empty if only the main group is involved.'],
  },
  repeatable: {
    title: 'A list — add a row per entry',
    body: [
      'Use **+ Add** for each one. There is no limit, and an empty row is ignored rather than being an error.',
      'Completeness matters more than tidiness here: an extra name costs a search, a missing one costs a conflict nobody checked.',
    ],
  },
  tags: {
    title: 'A free list of names',
    body: [
      'Type a name and press Enter to add it, as many as you need. Nothing is looked up — these are written down exactly as you type them.',
    ],
  },
  optionsMultiSelect: {
    title: 'Tick everything that applies',
    body: ['More than one may be ticked. The list is maintained by the firm.'],
  },
  multicheck: {
    title: 'Tick everything that applies',
    body: ['More than one may be ticked.'],
  },
  areaOfLawSelect: {
    title: 'Area of law',
    body: ['The firm’s own classification list. Choosing Other asks you to say why.'],
  },
  address: {
    title: 'An address',
    body: ['Fill in the parts that apply. It is stored as one address, so it prints properly on the record.'],
  },
  money: {
    title: 'An amount',
    body: ['Figures only — the form adds the formatting. An estimate is fine where an exact figure is not known yet.'],
  },
  percent: {
    title: 'A percentage',
    body: ['A number between 0 and 100. The % sign is added for you.'],
  },
  date: { title: 'A date', body: ['Use the picker, or type it — both work.'] },
  email: { title: 'An email address', body: ['One address. It is checked for the obvious mistakes, not verified.'] },
  tel: { title: 'A telephone number', body: ['Any format the firm uses is fine.'] },
  yesno: {
    title: 'Yes or no',
    body: ['Some answers here open a follow-up question. That is why a page can grow as you fill it in.'],
  },
  textarea: { title: 'A longer answer', body: ['Write as much as is useful. It prints in full on the record.'] },
  select: { title: 'One from the list', body: ['The options are maintained by the firm.'] },
  attachments: {
    title: 'Attachments',
    body: ['Files attached here travel with the matter and appear on the record.'],
  },
};

/** Marker text appended to whatever the field is, since this is the commonest question of all. */
function requirementNote(field) {
  if (field.requirement === 'required') {
    return 'This one is **required** — you cannot finish without it.';
  }
  if (field.requirement === 'soft_required') {
    return 'This one is **usually provided**. You can leave it blank, but you will be asked to say briefly why, and that reason is kept with the record.';
  }
  return '';
}

/**
 * The guide's content for one field.
 *
 * @param {object} field    an effective field: path, type, label, help, requirement
 * @param {object} [config] the form config, for rules whose numbers come from settings
 * @returns {{title: string, body: string[], known: boolean}}
 */
export function guidanceForField(field, config = {}) {
  if (!field) return null;

  const known = BY_PATH[field.path] || BY_TYPE[field.type] || null;
  const body = known ? [...known.body] : [];

  // The cap is read from settings/general rather than written here, so this line can never
  // disagree with the validation that enforces it.
  if (field.type === 'originationSplit') {
    const cap = originationCapPercent(config.general);
    body.push(`No one attorney may hold more than **${cap}%**, so a matter one person would otherwise take all the credit for has to be shared.`);
  }

  // The field's own help text is the firm's words about their own question, and outranks anything
  // generic we could say about the control.
  if (field.help) body.unshift(field.help);

  const note = requirementNote(field);
  if (note) body.push(note);

  if (!body.length) {
    return {
      title: field.label || 'This question',
      body: ['This is one of your firm’s own questions. Answer it as it is written.'],
      known: false,
    };
  }

  return {
    title: field.label || (known && known.title) || 'This question',
    body,
    known: Boolean(known),
  };
}

export { BY_PATH, BY_TYPE };
